import React, { useContext, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import MyContext from "../../context/MyContext";
import ProjectContext from "../../context/ProjectContext";
import AuthContext from "../../context/userAuth.context";
import { pushUndo } from "../../Utils/undo";
import {
  MOTION_EASINGS,
  MOTION_SPEEDS,
  MIN_MOTION_SECONDS,
  applyLayerMotion,
  clearLayerMotion,
  convertOldAtoZ,
  defaultMotion,
  durationForSpeed,
  hasMotion,
  layerSeconds,
  motionTotalSeconds,
  walkActions,
  MOTION_PATHS,
  motionPoint,
  defaultHandle,
} from "../../Utils/layerMotion";

// "Move A → Z" on the editor canvas.
//
// - The layer's own position is A. Drag the layer (or the A marker) to change it.
// - Drag the Z marker (or the dashed box) to set where the layer ends up.
// - The panel sets the speed (how long the move takes), when it starts and how it moves.
// - While the timeline plays, the layers move here exactly as in the preview and the export
//   (Utils/layerMotion.js; the export page uses the same maths).
//
// Opened from a layer's settings ("Move"), which sends the window event "toons:edit-motion".
// A selected layer that already moves shows its path, so Z can be adjusted any time.

export const EDIT_MOTION_EVENT = "toons:edit-motion";

const COLORS = { path: "#296AFF", z: "#FF6B2C", a: "#1FB36B", h: "#8E44EC" };

const stop = (e) => e.stopPropagation();

const MotionOverlay = ({ canvasRef }) => {
  const {
    LayersData,
    CanvasDisplay,
    EditorZoom,
    onePxEquivalent,
    clone,
    ChangeCount,
    setChangeCount,
  } = useContext(MyContext);
  const { setLayersData } = useContext(ProjectContext);
  const { authenticationData } = useContext(AuthContext);
  const assetBase = authenticationData && authenticationData.ApiBaseURL;

  const [editKey, setEditKey] = useState(null);
  const [drag, setDrag] = useState(null); // { what: "a" | "z", startX, startY, dx, dy }
  const [, setResized] = useState(0);
  const [collapsed, setCollapsed] = useState(false);
  const [drawing, setDrawing] = useState(null); // draw-a-path mode: { stroke: null | [points] }
  const testRun = useRef(null);
  const layersRef = useRef(LayersData);
  layersRef.current = LayersData;

  const zoom = (Number(EditorZoom) || 100) / 100;
  const onePx = Number(onePxEquivalent) || Number(localStorage.getItem("onePxEquivalent")) || 0;

  // Canvas size on screen (changes with zoom and video shape). Read from the element on every render
  // and when it is needed (a ResizeObserver only re-renders; its callbacks can be held back while
  // the window is in the background).
  const measure = () => {
    const el = canvasRef && canvasRef.current;
    return el ? { w: el.clientWidth, h: el.clientHeight } : { w: 0, h: 0 };
  };
  useEffect(() => {
    const el = canvasRef && canvasRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(() => setResized((n) => n + 1));
    ro.observe(el);
    return () => ro.disconnect();
  }, [canvasRef]);
  const size = measure();

  // Canvas size in layer units (layer x/y/width/height are stored at 100% zoom).
  const W = size.w / zoom;
  const H = size.h / zoom;

  // Every change can be undone with Ctrl+Z; slider moves of the same setting make one undo step.
  const undoCtx = useContext(MyContext);
  const commit = (key, change, groupKey) => {
    pushUndo(undoCtx, layersRef.current, groupKey ? "motion:" + key + ":" + groupKey : null);
    const array = Array.from(layersRef.current);
    const i = array.findIndex((l) => l.key === key);
    if (i < 0) return;
    const layer = clone ? clone(array[i]) : JSON.parse(JSON.stringify(array[i]));
    change(layer);
    array[i] = layer;
    setLayersData(array);
    if (setChangeCount) setChangeCount((ChangeCount || 0) + 1);
  };

  // Old projects: the editor-only "A to Z" end box becomes a Motion.
  useEffect(() => {
    if (!(W > 0) || !(H > 0) || !LayersData || !LayersData.some((l) => l && l.AzData)) return;
    const array = Array.from(LayersData);
    if (convertOldAtoZ(array, W, H, onePx)) setLayersData(array);
  }, [LayersData, W, H]);

  // "Move" in a layer's settings.
  useEffect(() => {
    const open = (e) => {
      const key = e.detail && e.detail.key;
      const layer = layersRef.current.find((l) => l.key === key);
      const now = measure();
      const cw = now.w / zoom;
      const chh = now.h / zoom;
      if (!layer || !(cw > 0) || !(chh > 0)) return;
      if (!hasMotion(layer)) {
        commit(key, (l) => {
          l.Motion = defaultMotion(l, cw, chh, onePx);
        });
      }
      setEditKey(key);
    };
    window.addEventListener(EDIT_MOTION_EVENT, open);
    return () => window.removeEventListener(EDIT_MOTION_EVENT, open);
  }, [zoom, onePx]);

  useEffect(() => {
    if (!editKey) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") {
        setDrawing(null);
        setEditKey(null);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [editKey]);

  // Playback in the editor: move the layers with the timeline's play head.
  useEffect(() => {
    if (CanvasDisplay) {
      clearLayerMotion("Outer-");
      return undefined;
    }
    let frame = null;
    const head = document.getElementById("tickerHead");
    const tick = () => {
      const m = head && /translate\(\s*(-?[\d.]+)px/.exec(head.style.transform || "");
      const px = m ? parseFloat(m[1]) : 0;
      applyLayerMotion(layersRef.current, px * onePx, onePx, "Outer-", assetBase);
      frame = window.requestAnimationFrame(tick);
    };
    frame = window.requestAnimationFrame(tick);
    return () => {
      window.cancelAnimationFrame(frame);
      clearLayerMotion("Outer-");
    };
  }, [CanvasDisplay, onePx]);

  useEffect(
    () => () => {
      if (testRun.current) window.cancelAnimationFrame(testRun.current.frame);
    },
    []
  );

  const selected =
    (editKey != null && LayersData.find((l) => l.key === editKey)) ||
    LayersData.find((l) => l.Editable && hasMotion(l));
  if (!CanvasDisplay || !selected || !hasMotion(selected) || !(size.w > 0)) return null;

  const saved = selected.Motion;
  const editing = editKey === selected.key;
  const lw = (Number(selected.width) || 0) * zoom;
  const lh = (Number(selected.height) || 0) * zoom;
  const path = saved.path || "line";

  // Everything below is in canvas pixels on screen; positions are the layer's top-left corner.
  const A0 = { x: (Number(selected.x) || 0) * zoom, y: (Number(selected.y) || 0) * zoom };
  const toF = (q) => ({ xf: q.x / size.w, yf: q.y / size.h });
  // The move as it looks while something is being dragged (saved once the mouse is released).
  const live = { ...saved, points: Array.isArray(saved.points) ? saved.points.map((q) => ({ ...q })) : [] };
  let A = A0;
  if (drag && drag.dx !== undefined) {
    const d = { x: drag.dx, y: drag.dy };
    if (drag.what === "a") A = { x: A0.x + d.x, y: A0.y + d.y };
    if (drag.what === "z") {
      live.toXf = saved.toXf + d.x / size.w;
      live.toYf = saved.toYf + d.y / size.h;
    }
    if (drag.what === "h") {
      live.hXf = saved.hXf + d.x / size.w;
      live.hYf = saved.hYf + d.y / size.h;
    }
    if (drag.what === "pt" && live.points[drag.index]) {
      live.points[drag.index] = {
        xf: saved.points[drag.index].xf + d.x / size.w,
        yf: saved.points[drag.index].yf + d.y / size.h,
      };
    }
  }
  const Z = { x: Number(live.toXf) * size.w, y: Number(live.toYf) * size.h };
  const center = (q) => ({ x: q.x + lw / 2, y: q.y + lh / 2 });
  const samples = [];
  for (let i = 0; i <= 80; i++) samples.push(center(motionPoint(live, A, size.w, size.h, i / 80)));
  if (drawing && drawing.stroke && drawing.stroke.length > 1) {
    samples.length = 0;
    drawing.stroke.forEach((q) => samples.push(q));
  }
  let pathLength = 0;
  for (let i = 1; i < samples.length; i++) pathLength += Math.hypot(samples[i].x - samples[i - 1].x, samples[i].y - samples[i - 1].y);
  const distanceWidths = pathLength / size.w;
  const seconds = layerSeconds(selected, onePx);
  const totalMove = motionTotalSeconds(saved);
  const ends = (Number(saved.delay) || 0) + (Number.isFinite(totalMove) ? totalMove : 0);
  const isCharacter = selected.type === "Character";
  const actions = isCharacter ? walkActions(selected) : [];
  const a = center(A);
  const z = center(Z);
  const hd = Number.isFinite(Number(live.hXf)) ? center({ x: live.hXf * size.w, y: live.hYf * size.h }) : null;

  const canvasPoint = (e) => {
    const r = canvasRef.current.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };

  const startDrag = (what, index) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.setPointerCapture && e.currentTarget.setPointerCapture(e.pointerId);
    setDrag({ what, index, startX: e.clientX, startY: e.clientY, dx: 0, dy: 0 });
  };
  const moveDrag = (e) => {
    if (!drag) return;
    e.preventDefault();
    setDrag({ ...drag, dx: e.clientX - drag.startX, dy: e.clientY - drag.startY });
  };
  const endDrag = (e) => {
    if (!drag) return;
    e.stopPropagation();
    const { what, dx, dy } = drag;
    setDrag(null);
    if (Math.abs(dx) < 1 && Math.abs(dy) < 1) return;
    if (what === "a") {
      commit(selected.key, (l) => {
        l.x = (Number(l.x) || 0) + dx / zoom;
        l.y = (Number(l.y) || 0) + dy / zoom;
        l.originalX = l.x;
        l.originalY = l.y;
      });
    } else {
      commit(selected.key, (l) => {
        l.Motion = { ...l.Motion, toXf: live.toXf, toYf: live.toYf, hXf: live.hXf, hYf: live.hYf, points: live.points };
      });
    }
  };

  const setMotion = (patch, groupKey) =>
    commit(
      selected.key,
      (l) => {
        l.Motion = { ...l.Motion, ...patch };
      },
      groupKey
    );

  const choosePath = (value) => {
    const patch = { path: value };
    if ((value === "arc" || value === "bounce") && !Number.isFinite(Number(saved.hXf))) {
      Object.assign(patch, defaultHandle(A0, Z, size.w, size.h));
    }
    if (value === "bounce" && !saved.bounces) Object.assign(patch, { bounces: 3, bounciness: 0.6 });
    if (value !== "line" && value !== "custom") patch.easing = "linear";
    if (value === "custom" && !(saved.points && saved.points.length)) {
      // Start from the current shape, with a few points to drag.
      const pts = [1, 2, 3].map((i) => toF(motionPoint(saved, A0, size.w, size.h, i / 4)));
      patch.points = pts;
    }
    setMotion(patch);
  };

  const addPoint = () => {
    const pts = [A0].concat((saved.points || []).map((q) => ({ x: q.xf * size.w, y: q.yf * size.h })), [Z]);
    let best = 0;
    for (let i = 1; i < pts.length - 1; i++) {
      if (Math.hypot(pts[i + 1].x - pts[i].x, pts[i + 1].y - pts[i].y) > Math.hypot(pts[best + 1].x - pts[best].x, pts[best + 1].y - pts[best].y)) best = i;
    }
    const mid = { x: (pts[best].x + pts[best + 1].x) / 2, y: (pts[best].y + pts[best + 1].y) / 2 };
    const next = (saved.points || []).slice();
    next.splice(best, 0, toF(mid));
    setMotion({ path: "custom", points: next });
  };
  const removePoint = (index) => {
    const next = (saved.points || []).filter((_, i) => i !== index);
    setMotion(next.length ? { points: next } : { points: [], path: "line" });
  };

  // Drawing a path with the mouse: press anywhere on the canvas and draw; the move follows the
  // line, starting at A and ending where the mouse is released.
  const drawDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.setPointerCapture && e.currentTarget.setPointerCapture(e.pointerId);
    setDrawing({ stroke: [a, canvasPoint(e)] });
  };
  const drawMove = (e) => {
    if (!drawing || !drawing.stroke) return;
    const q = canvasPoint(e);
    const last = drawing.stroke[drawing.stroke.length - 1];
    if (Math.hypot(q.x - last.x, q.y - last.y) < 4) return;
    setDrawing({ stroke: drawing.stroke.concat([q]) });
  };
  const drawUp = (e) => {
    e.stopPropagation();
    const stroke = drawing && drawing.stroke;
    setDrawing(null);
    if (!stroke || stroke.length < 3) return;
    // Evenly spaced points along the drawn line (top-left corners).
    let len = 0;
    const d = [0];
    for (let i = 1; i < stroke.length; i++) {
      len += Math.hypot(stroke[i].x - stroke[i - 1].x, stroke[i].y - stroke[i - 1].y);
      d.push(len);
    }
    if (len < 10) return;
    const count = Math.max(2, Math.min(10, Math.round(len / 40)));
    const at = (dist) => {
      let i = 1;
      while (i < d.length - 1 && d[i] < dist) i++;
      const seg = d[i] - d[i - 1] || 1;
      const t = (dist - d[i - 1]) / seg;
      return { x: stroke[i - 1].x + (stroke[i].x - stroke[i - 1].x) * t - lw / 2, y: stroke[i - 1].y + (stroke[i].y - stroke[i - 1].y) * t - lh / 2 };
    };
    const points = [];
    for (let k = 1; k < count; k++) points.push(toF(at((len * k) / count)));
    const end = at(len);
    setMotion({ path: "custom", points, toXf: end.x / size.w, toYf: end.y / size.h, easing: saved.easing || "linear" });
  };

  // Plays just this move on the canvas (without the timeline).
  const tryMove = () => {
    const el = document.getElementById("Outer-" + selected.AnimationId);
    if (!el) return;
    if (testRun.current) window.cancelAnimationFrame(testRun.current.frame);
    const started = performance.now();
    const once = (Number(saved.duration) || 0) + (Number(saved.pause) || 0);
    const total = (Number.isFinite(totalMove) ? totalMove : 2 * once) * 1000 + 600;
    const layer = { ...selected, startPositionOnTimeline: 0, Motion: { ...saved, delay: 0 } };
    const run = { frame: null };
    const step = (now) => {
      const t = now - started;
      applyLayerMotion([layer], Math.min(t, total), 1, "Outer-", assetBase);
      if (t < total) run.frame = window.requestAnimationFrame(step);
      else applyLayerMotion([layer], -1, 1, "Outer-", assetBase); // back to A
    };
    run.frame = window.requestAnimationFrame(step);
    testRun.current = run;
  };

  // The settings go in the canvas corner furthest from the path, so they cover it as little as possible.
  const PANEL_W = 236;
  const PANEL_H = collapsed ? 34 : Math.min(size.h - 16, 420);
  const corners = [
    { left: size.w - PANEL_W - 8, top: 8 },
    { left: 8, top: 8 },
    { left: size.w - PANEL_W - 8, top: Math.max(8, size.h - PANEL_H - 8) },
    { left: 8, top: Math.max(8, size.h - PANEL_H - 8) },
  ];
  const keyPoints = samples.filter((_, i) => i % 8 === 0).concat([a, z]);
  const distance = (c) =>
    Math.min(
      ...keyPoints.map((pt) =>
        Math.hypot(
          Math.max(c.left - pt.x, 0, pt.x - (c.left + PANEL_W)),
          Math.max(c.top - pt.y, 0, pt.y - (c.top + PANEL_H))
        )
      )
    );
  const panelAt = corners.reduce((best, c) => (distance(c) > distance(best) ? c : best), corners[0]);

  // Next to the canvas when there is room (so the settings never cover the canvas), otherwise in
  // the canvas corner furthest from the path.
  let outside = null;
  const canvasBox = canvasRef.current ? canvasRef.current.getBoundingClientRect() : null;
  if (canvasBox) {
    const top = Math.max(8, canvasBox.top);
    const maxHeight = Math.max(160, window.innerHeight - top - 8);
    if (window.innerWidth - canvasBox.right >= PANEL_W + 16) outside = { left: canvasBox.right + 8, top, maxHeight };
    else if (canvasBox.left >= PANEL_W + 16) outside = { left: canvasBox.left - PANEL_W - 8, top, maxHeight };
  }
  const placePanel = (panel) => (outside ? ReactDOM.createPortal(panel, document.body) : panel);

  // The path (stops just before Z so the arrow head stays visible).
  let cut = samples.length - 1;
  while (cut > 1 && Math.hypot(samples[cut].x - z.x, samples[cut].y - z.y) < 15) cut--;
  const shown = samples.slice(0, cut + 1);
  const tip = shown[shown.length - 1];
  const before = shown[Math.max(0, shown.length - 2)];
  const angle = Math.atan2(tip.y - before.y, tip.x - before.x);
  const head = 12;
  const arrow = [
    [tip.x, tip.y],
    [tip.x - head * Math.cos(angle - 0.45), tip.y - head * Math.sin(angle - 0.45)],
    [tip.x - head * Math.cos(angle + 0.45), tip.y - head * Math.sin(angle + 0.45)],
  ]
    .map((q) => q.join(","))
    .join(" ");
  const line = shown.map((q) => q.x.toFixed(1) + "," + q.y.toFixed(1)).join(" ");

  // Outside the settings (layer only selected) A is just a label, so the layer itself can still be
  // clicked and dragged; Z can always be dragged.
  const marker = (p, label, color, what, title, interactive = true, extra = {}, index) => (
    <div
      key={what + (index === undefined ? "" : index)}
      title={title}
      onPointerDown={startDrag(what, index)}
      onPointerMove={moveDrag}
      onPointerUp={endDrag}
      onMouseDown={stop}
      onDoubleClick={what === "pt" ? () => removePoint(index) : undefined}
      style={{
        position: "absolute",
        left: p.x - 13,
        top: p.y - 13,
        width: 26,
        height: 26,
        borderRadius: "50%",
        background: color,
        color: "#fff",
        font: "700 13px/26px sans-serif",
        textAlign: "center",
        cursor: drag ? "grabbing" : "grab",
        pointerEvents: interactive && !drawing ? "auto" : "none",
        opacity: interactive ? 1 : 0.85,
        boxShadow: "0 1px 4px rgba(0,0,0,.45)",
        border: "2px solid #fff",
        userSelect: "none",
        touchAction: "none",
        boxSizing: "border-box",
        ...extra,
      }}
    >
      {label}
    </div>
  );

  const button = (label, onClick, extra = {}, title) => (
    <button
      type="button"
      title={title}
      onClick={onClick}
      style={{
        border: "1px solid #c9d3e6",
        background: "#fff",
        borderRadius: 4,
        padding: "2px 8px",
        fontSize: 12,
        cursor: "pointer",
        ...extra,
      }}
    >
      {label}
    </button>
  );
  const slider = (label, value, min, max, step, onChange) => (
    <>
      <label style={{ display: "block", marginBottom: 2 }}>{label}</label>
      <input type="range" min={min} max={max} step={step} value={value} onChange={(e) => onChange(Number(e.target.value))} style={{ width: "100%", marginBottom: 6 }} />
    </>
  );

  const zLeft = Number(live.toXf) * size.w;
  const zTop = Number(live.toYf) * size.h;

  return (
    <div
      className="MotionOverlay"
      onMouseDown={stop}
      style={{ position: "absolute", inset: 0, zIndex: 4000, pointerEvents: "none" }}
    >
      <svg width={size.w} height={size.h} style={{ position: "absolute", left: 0, top: 0, overflow: "visible" }}>
        <rect
          x={zLeft}
          y={zTop}
          width={lw}
          height={lh}
          fill="rgba(255,107,44,0.08)"
          stroke={COLORS.z}
          strokeDasharray="6 4"
          strokeWidth="1.5"
          style={{ pointerEvents: editing && !drawing ? "auto" : "none", cursor: "grab" }}
          onPointerDown={startDrag("z")}
          onPointerMove={moveDrag}
          onPointerUp={endDrag}
        />
        {hd && (path === "arc" || path === "bounce") && (
          <line x1={(a.x + z.x) / 2} y1={(a.y + z.y) / 2} x2={hd.x} y2={hd.y} stroke={COLORS.h} strokeWidth="1" strokeDasharray="3 3" opacity="0.7" />
        )}
        <polyline points={line} fill="none" stroke="#fff" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" opacity="0.8" />
        <polyline points={line} fill="none" stroke={COLORS.path} strokeWidth="2.5" strokeDasharray="8 5" strokeLinejoin="round" />
        <polygon points={arrow} fill={COLORS.path} stroke="#fff" strokeWidth="1" />
      </svg>

      {/* Draw-a-path mode: the whole canvas takes the mouse. */}
      {editing && drawing && (
        <div
          onPointerDown={drawDown}
          onPointerMove={drawMove}
          onPointerUp={drawUp}
          style={{ position: "absolute", inset: 0, cursor: "crosshair", pointerEvents: "auto", background: "rgba(41,106,255,0.04)", touchAction: "none" }}
        >
          <div style={{ position: "absolute", left: "50%", top: 8, transform: "translateX(-50%)", background: COLORS.path, color: "#fff", borderRadius: 12, padding: "3px 10px", fontSize: 12, fontFamily: "sans-serif", whiteSpace: "nowrap" }}>
            Press and draw the path with the mouse
          </div>
        </div>
      )}

      {marker(a, "A", COLORS.a, "a", "Start (A): drag to move the layer", editing)}
      {editing &&
        path === "custom" &&
        live.points.map((q, i) =>
          marker(center({ x: q.xf * size.w, y: q.yf * size.h }), String(i + 1), COLORS.h, "pt", "Drag to shape the path; double-click to remove", true, { width: 20, height: 20, left: center({ x: q.xf * size.w, y: q.yf * size.h }).x - 10, top: center({ x: q.xf * size.w, y: q.yf * size.h }).y - 10, font: "700 10px/16px sans-serif" }, i)
        )}
      {editing && hd && (path === "arc" || path === "bounce") &&
        marker(hd, "▲", COLORS.h, "h", path === "arc" ? "Drag to shape the throw (the highest point)" : "Drag to set the height and direction of the bounces", true, { borderRadius: 6, font: "700 11px/22px sans-serif" })}
      {marker(z, "Z", COLORS.z, "z", "End (Z): drag to where the layer should move")}

      {editing && placePanel(
        <div
          className="MotionPanel"
          onPointerDown={stop}
          onMouseDown={stop}
          onClick={stop}
          style={{
            position: outside ? "fixed" : "absolute",
            left: outside ? outside.left : panelAt.left,
            top: outside ? outside.top : panelAt.top,
            zIndex: 10000,
            width: PANEL_W,
            maxHeight: outside ? outside.maxHeight : Math.max(120, size.h - 16),
            overflowY: "auto",
            boxSizing: "border-box",
            background: "rgba(255,255,255,0.97)",
            borderRadius: 8,
            boxShadow: "0 4px 16px rgba(0,0,0,.25)",
            padding: "10px 12px",
            fontSize: 12,
            color: "#1d2433",
            pointerEvents: "auto",
            fontFamily: "sans-serif",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
            <strong style={{ fontSize: 13 }}>Move A → Z</strong>
            <span style={{ display: "flex", gap: 4 }}>
              {button(collapsed ? "▾" : "▴", () => setCollapsed(!collapsed), { padding: "2px 6px" })}
              {button("Done", () => { setDrawing(null); setEditKey(null); }, { background: COLORS.path, color: "#fff", border: "1px solid " + COLORS.path })}
            </span>
          </div>
          {!collapsed && (
          <>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 4, marginBottom: 8 }}>
            {MOTION_PATHS.map((o) => (
              <React.Fragment key={o.value}>
                {button(
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                    <span className="material-icons" style={{ fontSize: 15, width: 15, overflow: "hidden" }}>{o.icon}</span>
                    {o.label}
                  </span>,
                  () => (o.value === "custom" ? (choosePath("custom"), setDrawing({ stroke: null })) : (setDrawing(null), choosePath(o.value))),
                  path === o.value ? { background: "#eef3ff", border: "1px solid " + COLORS.path, color: COLORS.path, fontWeight: 600, padding: "4px 6px" } : { padding: "4px 6px" }
                )}
              </React.Fragment>
            ))}
          </div>

          <div style={{ color: "#5b6476", marginBottom: 8 }}>
            {path === "line" && <>Drag <b style={{ color: COLORS.a }}>A</b> (or the layer) and <b style={{ color: COLORS.z }}>Z</b>.</>}
            {path === "arc" && <>Drag <b style={{ color: COLORS.h }}>▲</b> to shape the throw in any direction, <b style={{ color: COLORS.z }}>Z</b> for where it lands.</>}
            {path === "bounce" && <>Drag <b style={{ color: COLORS.h }}>▲</b> for the height and direction of the hops.</>}
            {path === "custom" && (drawing ? <>Draw on the canvas: press, draw, release.</> : <>Drag the numbered points; double-click one to remove it.</>)}
          </div>

          {path === "custom" && (
            <div style={{ display: "flex", gap: 4, marginBottom: 8 }}>
              {button("✎ Draw again", () => setDrawing({ stroke: null }), { flex: 1 })}
              {button("+ Add point", addPoint, { flex: 1 })}
            </div>
          )}

          {path === "bounce" && (
            <>
              {slider(<>Bounces: <b>{saved.bounces || 3}</b></>, saved.bounces || 3, 1, 8, 1, (v) => setMotion({ bounces: v }, "bounces"))}
              {slider(<>Bounciness: <b>{Math.round((saved.bounciness || 0.6) * 100)}%</b></>, saved.bounciness || 0.6, 0.2, 1, 0.05, (v) => setMotion({ bounciness: v }, "bounciness"))}
            </>
          )}

          {isCharacter && (
            <div style={{ background: "#f5f7fb", borderRadius: 6, padding: "6px 8px", marginBottom: 8 }}>
              <label style={{ display: "block", marginBottom: 2 }}>While moving, play:</label>
              <select
                value={saved.walkFile || ""}
                onChange={(e) => setMotion({ walkFile: e.target.value || null })}
                style={{ width: "100%", fontSize: 12, marginBottom: 6 }}
              >
                <option value="">Its own action (no change)</option>
                {actions.map((o) => (
                  <option key={o.file} value={o.file}>
                    {o.name}
                    {o.walkLike ? " ★" : ""}
                  </option>
                ))}
              </select>
              <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                <input type="checkbox" checked={!!saved.autoFace} onChange={(e) => setMotion({ autoFace: e.target.checked })} />
                Turn to face where it goes
              </label>
              <div style={{ color: "#7a8396", marginTop: 2 }}>Use Flip so it faces its first direction; it turns by itself after that.</div>
              {!actions.some((o) => o.walkLike) && (
                <div style={{ color: "#7a8396", marginTop: 4 }}>This character has no walk action; it keeps its own action while moving.</div>
              )}
            </div>
          )}

          {slider(<>Speed: takes <b>{Number(saved.duration).toFixed(1)} s</b></>, Number(saved.duration), MIN_MOTION_SECONDS, Math.max(10, Math.ceil(seconds || 10)), 0.1, (v) => setMotion({ duration: v }, "duration"))}
          <div style={{ display: "flex", gap: 4, marginBottom: 8, marginTop: -2 }}>
            {MOTION_SPEEDS.map((sp) => (
              <React.Fragment key={sp.label}>
                {button(sp.label, () => setMotion({ duration: durationForSpeed(distanceWidths, sp.widthsPerSecond) }), { flex: 1 })}
              </React.Fragment>
            ))}
          </div>

          {slider(<>Starts <b>{Number(saved.delay || 0).toFixed(1)} s</b> after the layer appears</>, Number(saved.delay || 0), 0, Math.max(0, Math.floor(((seconds || 10) - MIN_MOTION_SECONDS) * 10) / 10), 0.1, (v) => setMotion({ delay: v }, "delay"))}

          {slider(<>Spin while moving: <b>{Number(saved.spin || 0)}</b> {Math.abs(Number(saved.spin || 0)) === 1 ? "turn" : "turns"}</>, Number(saved.spin || 0), -5, 5, 0.5, (v) => setMotion({ spin: v }, "spin"))}

          {slider(<>Size at Z: <b>{Math.round((saved.scaleTo === undefined ? 1 : saved.scaleTo) * 100)}%</b></>, saved.scaleTo === undefined ? 1 : saved.scaleTo, 0.1, 3, 0.05, (v) => setMotion({ scaleTo: v }, "scaleTo"))}
          {slider(<>Opacity at Z: <b>{Math.round((saved.opacityTo === undefined ? 1 : saved.opacityTo) * 100)}%</b></>, saved.opacityTo === undefined ? 1 : saved.opacityTo, 0, 1, 0.05, (v) => setMotion({ opacityTo: v }, "opacityTo"))}

          <div style={{ background: "#f5f7fb", borderRadius: 6, padding: "6px 8px", marginBottom: 8 }}>
            <label style={{ display: "block", marginBottom: 2 }}>Repeat</label>
            <select
              value={saved.repeat || "once"}
              onChange={(e) => setMotion({ repeat: e.target.value, times: e.target.value === "once" ? 1 : saved.times === undefined || saved.times === 1 ? 2 : saved.times })}
              style={{ width: "100%", fontSize: 12, marginBottom: 6 }}
            >
              <option value="once">Once (stays at Z)</option>
              <option value="pingpong">There and back (A → Z → A …)</option>
              <option value="loop">Loop (starts again at A)</option>
            </select>
            {(saved.repeat === "pingpong" || saved.repeat === "loop") && (
              <>
                {slider(
                  <>Trips: <b>{Number(saved.times) ? saved.times : "until the layer ends"}</b></>,
                  Number(saved.times) || 0,
                  0,
                  12,
                  1,
                  (v) => setMotion({ times: v }, "times")
                )}
                {slider(<>Wait at each end: <b>{Number(saved.pause || 0).toFixed(1)} s</b></>, Number(saved.pause || 0), 0, 5, 0.1, (v) => setMotion({ pause: v }, "pause"))}
              </>
            )}
          </div>

          <select
            value={saved.easing || "linear"}
            onChange={(e) => setMotion({ easing: e.target.value })}
            style={{ width: "100%", marginBottom: 8, fontSize: 12 }}
          >
            {MOTION_EASINGS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>

          {seconds > 0 && ends > seconds + 0.05 && (
            <div style={{ color: "#b4540a", marginBottom: 8 }}>
              The layer is only {seconds.toFixed(1)} s long, so it disappears before the move finishes. Make the move faster, use fewer trips, or make the layer longer on the timeline.
            </div>
          )}

          <div style={{ display: "flex", gap: 6 }}>
            {button("▶ Try it", tryMove, { flex: 1 })}
            {button(
              "Remove move",
              () => {
                commit(selected.key, (l) => {
                  delete l.Motion;
                });
                setDrawing(null);
                setEditKey(null);
              },
              { flex: 1, color: "#c62828" }
            )}
          </div>
          </>
          )}
        </div>
      )}
    </div>
  );
};

export default MotionOverlay;

/** Opens the A → Z editor for a layer (used by the layer settings). */
export function editLayerMotion(key) {
  window.dispatchEvent(new CustomEvent(EDIT_MOTION_EVENT, { detail: { key } }));
}